import "./css/App.css";
import { useEffect, useState } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import FormCard from "./components/FormCard";
import { useStoreState } from "easy-peasy";
import GaugeChart from "react-gauge-chart";
import { AnimatePresence, motion } from "framer-motion";
import { PhoneIphone, DesktopWindows } from "@mui/icons-material";
import { Alert, Card, Snackbar } from "@mui/material";

import {
	LockClosedIcon,
	RefreshIcon,
	ClockIcon,
	ShieldCheckIcon
} from "@heroicons/react/outline";
import { ArrowSmDownIcon, ArrowSmUpIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import dayjs from "dayjs";

function classNames(...classes) {
	return classes.filter(Boolean).join(" ");
}

function App() {
	const [openSnackbar, setOpenSnackbar] = useState(false);

	const { psi } = useStoreState(store => ({ psi: store.psiData.psi }));

	useEffect(() => {
		if (psi.message === "error") {
			setOpenSnackbar(true);
		}
	}, [psi]);

	const handleClose = (event, reason) => {
		if (reason === "clickaway") {
			return;
		}

		setOpenSnackbar(false);
	};

	return (
		<>
			{openSnackbar && <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
				<Alert onClose={handleClose} severity="warning" sx={{ width: "110%" }}>
					There is no such domain.
				</Alert>
			</Snackbar>}
			<div className="bg-[#f9fafb] py-8">
				<div className="container audit-wrapper mx-auto">
					<div className="">
						<Header />
						<FormCard />
						<AnimatePresence>
							{psi.message === "success" && <motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: "auto" }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 1 }}
							>
								<div className="max-w-7xl mx-auto mt-10">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<h2 className="flex justify-center text-[#616069] items-center text-4xl mb-7"><PhoneIphone sx={{ fontSize: 30, marginRight: "3px" }} />Mobile
											</h2>
											<Card className="p-10" sx={{
												bgcolor: "#f9fafb"
											}}>
												<h3 className="flex justify-center text-[#616069] font-semibold text-2xl mb-5">Page Speed Insights (PSI)</h3>
												<GaugeChart
													id="gauge-chart-desktop"
													nrOfLevels={420}
													arcsLength={[0.33, 0.33, 0.33]}
													colors={["#f1664e", "#f5b22b", "#5fd37c"]}
													needleColor={"#d2d4d5"}
													needleBaseColor={"#acacac"}
													textColor={"#212121"}
													percent={psi.results.mobile.data.lighthouseResult.categories.performance.score}
													arcPadding={0.02}
													formatTextValue={value => value + ""}
												/>
											</Card>
											{/* Core Web Vitals Assessment */}
											<div className="bg-white shadow overflow-hidden sm:rounded-lg mt-4">
												<div className="px-4 py-5 sm:px-6 flex flex-column items-center">
													<PhoneIphone sx={{ fontSize: 30, marginRight: "3px", color: "#616069" }} />
													<div>
														<h3 className="text-lg leading-6 font-medium text-gray-900">Core Web Vitals Assessment</h3>
														<p className="mt-1 max-w-2xl text-sm text-gray-500">Captured at {psi.results.mobile.headers.date}</p>
													</div>
												</div>
												<div className="border-t border-gray-200">
													<dl>
														<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">First Contentful Paint</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.mobile.data.lighthouseResult.audits["first-contentful-paint"].displayValue}</dd>
														</div>
														<div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Time to Interactive</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.mobile.data.lighthouseResult.audits["interactive"].displayValue}</dd>
														</div>
														<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Speed Index</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.mobile.data.lighthouseResult.audits["speed-index"].displayValue}</dd>
														</div>
														<div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Total Blocking Time</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.mobile.data.lighthouseResult.audits["total-blocking-time"].displayValue}</dd>
														</div>
														<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Largest Contentful Paint</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.mobile.data.lighthouseResult.audits["largest-contentful-paint"].displayValue}</dd>
														</div>
														<div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Cumulative Layout Shift</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.mobile.data.lighthouseResult.audits["cumulative-layout-shift"].displayValue}</dd>
														</div>
													</dl>
												</div>
											</div>
										</div>
										<div>
											<h2 className="flex justify-center text-[#616069] items-center text-4xl mb-7"><DesktopWindows sx={{ fontSize: 30, marginRight: "7px" }} />Desktop
											</h2>
											<Card className="p-10" sx={{
												bgcolor: "#f9fafb"
											}}>
												<h3 className="flex justify-center text-[#616069] font-semibold text-2xl mb-5">Page Speed Insights (PSI)</h3>
												<GaugeChart
													id="gauge-chart-desktop"
													nrOfLevels={420}
													arcsLength={[0.33, 0.33, 0.33]}
													colors={["#f1664e", "#f5b22b", "#5fd37c"]}
													needleColor={"#d2d4d5"}
													needleBaseColor={"#9e9e9e"}
													textColor={"#212121"}
													percent={psi.results.desktop.data.lighthouseResult.categories.performance.score}
													arcPadding={0.02}
													formatTextValue={value => value + ""}
												/>
											</Card>
											{/* Core Web Vitals Assessment */}
											<div className="bg-white shadow overflow-hidden sm:rounded-lg mt-4">
												<div className="px-4 py-5 sm:px-6 flex flex-column items-center">
													<DesktopWindows sx={{ fontSize: 30, marginRight: "7px", color: "#616069" }} />
													<div>
														<h3 className="text-lg leading-6 font-medium text-gray-900">Core Web Vitals Assessment</h3>
														<p className="mt-1 max-w-2xl text-sm text-gray-500">Captured at {psi.results.desktop.headers.date}</p>
													</div>
												</div>
												<div className="border-t border-gray-200">
													<dl>
														<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">First Contentful Paint</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.desktop.data.lighthouseResult.audits["first-contentful-paint"].displayValue}</dd>
														</div>
														<div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Time to Interactive</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.desktop.data.lighthouseResult.audits["interactive"].displayValue}</dd>
														</div>
														<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Speed Index</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.desktop.data.lighthouseResult.audits["speed-index"].displayValue}</dd>
														</div>
														<div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Total Blocking Time</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.desktop.data.lighthouseResult.audits["total-blocking-time"].displayValue}</dd>
														</div>
														<div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Largest Contentful Paint</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.desktop.data.lighthouseResult.audits["largest-contentful-paint"].displayValue}</dd>
														</div>
														<div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
															<dt className="text-sm font-medium text-gray-500">Cumulative Layout Shift</dt>
															<dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{psi.results.desktop.data.lighthouseResult.audits["cumulative-layout-shift"].displayValue}</dd>
														</div>
													</dl>
												</div>
											</div>
										</div>
									</div>

									{psi.results.desktop.statusText === "OK" ?
										<div className="rounded-md shadow bg-green-50 p-4 mt-4">
											<div className="flex">
												<div className="flex-shrink-0">
													<CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
												</div>
												<div className="ml-3">
													<p className="text-sm font-medium text-green-800">Site is online.</p>
												</div>
											</div>
										</div>
										:
										<div className="rounded-md shadow bg-red-50 p-4 mt-4">
											<div className="flex">
												<div className="flex-shrink-0">
													<XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
												</div>
												<div className="ml-3">
													<p className="text-sm font-medium text-red-800">Site is offline.</p>
												</div>
											</div>
										</div>
									}

									<div className="relative bg-white shadow py-10 sm:py-10 lg:py-10 sm:rounded-lg mt-4">
										<div className="mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl">
											<h2 className="text-base font-semibold tracking-wider text-[#17837e] uppercase">Check Your SSL Certificates</h2>
											<p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
												Use our fast SSL Checker.
											</p>
											<p className="mt-5 max-w-prose mx-auto text-xl text-gray-500">
												Our diagnostic tool will locate SSL problems and verify your SSL Certificate installation.
											</p>
											<div className="mt-12">
												<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
													<div className="pt-6">
														<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
															<div className="-mt-6">
																<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<ShieldCheckIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
																</div>
																<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Days remaining</h3>
																<p className="mt-5 text-base text-gray-500">
																	{psi.results.ssl ? psi.results.ssl.daysRemaining : "0"}
																</p>
															</div>
														</div>
													</div>

													<div className="pt-6">
														<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
															<div className="-mt-6">
																<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<LockClosedIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
																</div>
																<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Valid status</h3>
																<div className="mt-5 text-base text-gray-500">
																	{psi.results.ssl &&
																		<div
																			className={classNames(
																				psi.results.ssl.valid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
																				"inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0"
																			)}
																		>
																			{psi.results.ssl.valid ?
																				<>
																					<ArrowSmUpIcon
																						className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-green-500"
																						aria-hidden="true"
																					/> Success
																				</> :
																				<>
																					<ArrowSmDownIcon
																						className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-red-500"
																						aria-hidden="true" /> Failed
																				</>
																			}
																		</div>}

																	{!psi.results.ssl && <div
																		className={classNames(
																			"bg-red-100 text-red-800",
																			"inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0"
																		)}
																	>
																		<>
																			<ArrowSmDownIcon
																				className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-red-500"
																				aria-hidden="true" /> Failed
																		</>
																	</div>}
																</div>
															</div>
														</div>
													</div>

													<div className="pt-6">
														<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
															<div className="-mt-6">
																<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<ClockIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
																</div>
																<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Valid From</h3>
																<p className="mt-5 text-base text-gray-500">
																	{psi.results.ssl && dayjs(psi.results.ssl.validFrom).format("YYYY-MM-DD HH:mm:ss")}
																	{!psi.results.ssl && "---"}
																</p>
															</div>
														</div>
													</div>

													<div className="pt-6">
														<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
															<div className="-mt-6">
																<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<RefreshIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
																</div>
																<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Valid to</h3>
																<p className="mt-5 text-base text-gray-500">
																	{psi.results.ssl && dayjs(psi.results.ssl.validTo).format("YYYY-MM-DD HH:mm:ss")}
																	{!psi.results.ssl && "---"}
																</p>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>

								</div>
							</motion.div>
							}
						</AnimatePresence>

						<AnimatePresence>
							{psi.message === "Site if offline" && <motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: "auto" }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 1 }}
							>
								<div className="max-w-7xl mx-auto mt-10">
									<div className="rounded-md shadow bg-red-50 p-4 mt-4">
										<div className="flex">
											<div className="flex-shrink-0">
												<XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
											</div>
											<div className="ml-3">
												<p className="text-sm font-medium text-red-800">Site is offline.</p>
											</div>
										</div>
									</div>
								</div>

							</motion.div>
							}
						</AnimatePresence>

						<AnimatePresence>
							{psi.message === "Invalid SSL" && <motion.div
								initial={{ opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: "auto" }}
								exit={{ opacity: 0, height: 0 }}
								transition={{ duration: 1 }}
							>

								<div className="max-w-7xl mx-auto mt-10">
									<div className="rounded-md shadow bg-red-50 p-4 mt-4">
										<div className="flex">
											<div className="flex-shrink-0">
												<XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
											</div>
											<div className="ml-3">
												<p className="text-sm font-medium text-red-800">Site is offline.</p>
											</div>
										</div>
									</div>
								</div>
								
								<div className="relative bg-white shadow py-10 sm:py-10 lg:py-10 sm:rounded-lg mt-4">
									<div className="mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl">
										<h2 className="text-base font-semibold tracking-wider text-[#17837e] uppercase">Check Your SSL Certificates</h2>
										<p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
											Use our fast SSL Checker.
										</p>
										<p className="mt-5 max-w-prose mx-auto text-xl text-gray-500">
											Our diagnostic tool will locate SSL problems and verify your SSL Certificate installation.
										</p>
										<div className="mt-12">
											<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
												<div className="pt-6">
													<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
														<div className="-mt-6">
															<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<ShieldCheckIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
															</div>
															<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Days remaining</h3>
															<p className="mt-5 text-base text-gray-500">
																{"0"}
															</p>
														</div>
													</div>
												</div>

												<div className="pt-6">
													<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
														<div className="-mt-6">
															<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<LockClosedIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
															</div>
															<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Valid status</h3>
															<div className="mt-5 text-base text-gray-500">
																<div
																	className={classNames(
																		"bg-red-100 text-red-800",
																		"inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0"
																	)}
																>
																	<>
																		<ArrowSmDownIcon
																			className="-ml-1 mr-0.5 flex-shrink-0 self-center h-5 w-5 text-red-500"
																			aria-hidden="true" /> Failed
																	</>
																</div>
															</div>
														</div>
													</div>
												</div>

												<div className="pt-6">
													<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
														<div className="-mt-6">
															<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<ClockIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
															</div>
															<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Valid From</h3>
															<p className="mt-5 text-base text-gray-500">
																{"---"}
															</p>
														</div>
													</div>
												</div>

												<div className="pt-6">
													<div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
														<div className="-mt-6">
															<div>
															  <span className="inline-flex items-center justify-center p-3 bg-[#17837e] rounded-md shadow-lg">
																<RefreshIcon className="h-6 w-6 text-white" aria-hidden="true" />
															  </span>
															</div>
															<h3 className="mt-8 text-lg font-medium text-gray-900 tracking-tight">Valid to</h3>
															<p className="mt-5 text-base text-gray-500">
																{"---"}
															</p>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

							</motion.div>
							}
						</AnimatePresence>

					</div>
				</div>
			</div>
		</>

	);
}

export default App;